import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, UserCircle2, Home, ArrowLeft, Sun, Moon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";

// URL du site vitrine WordPress dans lequel le configurateur est intégré (iframe).
// Peut être surchargé via .env : REACT_APP_MAIN_SITE_URL
const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL || "https://atelierinterhabitat.fr";

export default function Navbar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isAdmin = user?.role === "admin";
  const nav = [
    { to: "/", label: "Accueil", show: true },
    { to: "/configurateur", label: "Configurateur", show: !!user },
    { to: "/devis", label: "Mes devis", show: !!user },
    { to: "/mon-compte", label: "Mon compte", show: !!user },
    { to: "/admin", label: "Back-office", show: isAdmin },
    { to: "/integration", label: "Intégration", show: isAdmin },
  ].filter((n) => n.show);

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border"
    >
      {/* Bandeau de retour au site vitrine */}
      <div className="bg-primary/5 border-b border-primary/10">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-1.5">
          <a
            data-testid="back-to-site"
            href={MAIN_SITE_URL}
            target="_top"
            rel="noopener"
            className="inline-flex items-center gap-1.5 text-[12px] font-heading text-primary hover:text-accent transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Retour au site internet
          </a>
        </div>
      </div>
      <div className="w-full px-2 sm:px-6 lg:px-10 2xl:px-16 flex items-center justify-between h-16 gap-1 sm:gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-1 sm:gap-3 group shrink-0 min-w-0">
          {/* Logotype "Atelier Inter Habitat" — dessin CSS pur, sans image */}
          <div className="flex flex-col items-center leading-none">
            <span className="font-heading italic text-[10px] text-primary -mb-0.5">L&apos;Atelier</span>
            <div className="flex items-center gap-1">
              <Home className="w-4 h-4 text-primary" strokeWidth={1.8} />
              <span className="font-heading text-[10px] sm:text-[11px] tracking-[0.08em] uppercase text-primary font-bold">Inter Habitat</span>
            </div>
            <span className="font-heading text-[8px] tracking-[0.15em] uppercase text-foreground/60 mt-0.5">Menuiserie &amp; Agencement</span>
          </div>

          {/* Badge Made in Hauts-de-France — tricolore CSS pur */}
          <div
            className="hidden sm:flex items-center rounded overflow-hidden shadow-sm shrink-0"
            title="Made in Hauts-de-France"
            style={{ height: "28px" }}
          >
            <span className="h-full w-2" style={{ background: "#0055A4" }} />
            <span className="h-full w-2 bg-white" />
            <span className="h-full w-2" style={{ background: "#EF4135" }} />
            <span className="ml-1 pr-2 pl-1 font-heading text-[8px] leading-tight text-foreground/90 uppercase tracking-[0.05em]">
              Made in<br />Hauts-de-France
            </span>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-0.5 lg:gap-1 flex-1 min-w-0 [justify-content:safe_center] overflow-x-auto no-scrollbar">
          {nav.map((n) => {
            const active = pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={`shrink-0 whitespace-nowrap px-2.5 lg:px-4 py-2 rounded-full text-xs lg:text-sm transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/70 hover:text-foreground hover:bg-card"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
          {/* Toggle mode clair / sombre — visible pour tous, connectés ou non */}
          <button
            type="button"
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="w-8 h-8 rounded-full border border-border bg-card hover:border-primary hover:bg-primary/5 active:scale-95 transition flex items-center justify-center text-foreground/80 hover:text-primary"
            title={theme === "dark" ? "Passer en mode clair" : "Passer en mode sombre"}
            aria-label="Changer le thème"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <>
              <Link to="/mon-compte" className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <UserCircle2 className="w-4 h-4" />
                <span data-testid="nav-user">{user.contact_name}</span>
              </Link>
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                className="btn-secondary text-xs"
                title="Se déconnecter"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-secondary px-2.5 py-2 text-[11px] sm:px-6 sm:py-3 sm:text-xs">
                Connexion
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary px-2.5 py-2 text-[11px] sm:px-6 sm:py-3 sm:text-xs">
                S'inscrire
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
